import React, { useState, useEffect } from 'react';
import AutoFocusInput from './ui/AutoFocusInput';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface PasswordProtectionProps {
  onAccessGranted: () => void;
}

const PasswordProtection: React.FC<PasswordProtectionProps> = ({ onAccessGranted }) => {
  const [accessCode, setAccessCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if access is already granted
    const accessGranted = localStorage.getItem('thinkmate_access_granted');
    const accessGrantedAt = localStorage.getItem('thinkmate_access_granted_at');
    
    if (accessGranted && accessGrantedAt) {
      const now = Date.now();
      const grantedAt = parseInt(accessGrantedAt, 10);
      const sessionDuration = 24 * 60 * 60 * 1000; // 24 hours
      
      if (now - grantedAt < sessionDuration) {
        onAccessGranted();
        return;
      } else {
        // Session expired, clear storage
        localStorage.removeItem('thinkmate_access_granted');
        localStorage.removeItem('thinkmate_access_granted_at');
      }
    }
  }, [onAccessGranted]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/verify-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessCode }),
      });

      const data = await response.json();

      if (response.ok && data.accessGranted) {
        // Store access granted status
        localStorage.setItem('thinkmate_access_granted', 'true');
        localStorage.setItem('thinkmate_access_granted_at', Date.now().toString());
        onAccessGranted();
      } else {
        setError(data.msg || 'Invalid access code');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoClick = () => {
    window.location.reload();
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{
        background: `
          radial-gradient(circle at 20% 20%, rgba(255, 243, 212, 0.3) 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, rgba(243, 240, 255, 0.3) 0%, transparent 50%),
          #FAFAFA
        `,
        fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
      }}
    >
      {/* ThinkMate Logo Header (matches ambient branding) */}
      <header className="sticky top-0 z-50 w-full backdrop-blur-lg transition-all duration-300 bg-transparent">
        <div className="container mx-auto px-4 flex h-16 items-center">
          <button 
            onClick={handleLogoClick}
            className="flex items-center gap-2 font-bold text-xl hover:opacity-80 transition-opacity cursor-pointer focus:outline-none"
            aria-label="Refresh page"
            type="button"
            style={{ fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
          >
            <div className="size-8 rounded-lg flex items-center justify-center text-white" style={{ background: '#111111' }}>
              T
            </div>
            <span style={{ color: '#111111' }}>ThinkMate</span>
          </button>
        </div>
      </header>
      {/* Centered access form */}
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 mt-8">
          <div className="text-center mb-8">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: '#111111' }}
            >
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 
              className="text-2xl font-bold mb-2"
              style={{ 
                color: '#111111',
                fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}
            >
              Access Required
            </h1>
            <p 
              style={{ 
                color: '#6B7280',
                fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              }}
            >
              This door leads to caffeine, chaos, and creation.<br />
              Step in and shape the future.
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <AutoFocusInput
                id="accessCode"
                type="password"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:shadow-[0_0_0_4px_rgba(147,197,253,0.5)] transition-all duration-300"
                style={{
                  color: '#111111',
                  fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                }}
                placeholder="super top secret code"
                autoComplete="current-password"
                required
                disabled={isLoading}
              />
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p 
                  className="text-sm"
                  style={{ 
                    color: '#DC2626',
                    fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
                  }}
                >
                  {error}
                </p>
              </div>
            )}
            <button
              type="submit"
              disabled={isLoading || !accessCode.trim()}
              className="w-full py-3 px-4 font-medium focus:ring-2 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: '#111111',
                color: '#FFFFFF',
                borderRadius: '24px',
                border: 'none',
                fontFamily: "'Inter', 'Satoshi', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                transition: 'all 0.2s ease-in-out'
              }}
              onMouseEnter={(e) => {
                if (!isLoading && accessCode.trim()) {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Verifying...
                </div>
              ) : (
                'Continue to ThinkMate'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PasswordProtection; 